import { Capacitor } from "@capacitor/core";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "convex/react";

import { queryRef } from "../convex/refs";
import { addDays } from "../dateNav";
import { todayKey } from "../format";
import type { HeatmapResult } from "../habits/heatmap";
import { useDeviceId } from "../device/DeviceProvider";
import { MARKETING_DEMO_PREFERENCE_KEY } from "./constants";
import { isMarketingDemoEligible } from "./eligibility";
import { generateMarketingDemoDataset } from "./generator";
import { applyMarketingDemoOverlay, type MarketingDemoQueryName } from "./overlay";
import { resolveMarketingSeedApps } from "./seedApps";
import type { MarketingDemoDataset, MarketingSeedApp } from "./types";

type MarketingDemoContextValue = {
  isEligible: boolean;
  isActive: boolean;
  enabled: boolean;
  ready: boolean;
  dataset: MarketingDemoDataset | null;
  seedApps: MarketingSeedApp[];
  setEnabled: (next: boolean) => Promise<void>;
  disable: () => Promise<void>;
};

const MarketingDemoContext = createContext<MarketingDemoContextValue>({
  isEligible: false,
  isActive: false,
  enabled: false,
  ready: false,
  dataset: null,
  seedApps: [],
  setEnabled: async () => {},
  disable: async () => {},
});

const seedHeatmapRef = queryRef<
  { anonymousDeviceId: string; startDate: string; endDate: string },
  HeatmapResult
>("usageHeatmap:getHeatmap");

async function readMarketingDemoPreference(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      const { value } = await Preferences.get({ key: MARKETING_DEMO_PREFERENCE_KEY });
      return value === "true";
    } catch {
      return false;
    }
  }
  return localStorage.getItem(MARKETING_DEMO_PREFERENCE_KEY) === "true";
}

async function writeMarketingDemoPreference(enabled: boolean): Promise<void> {
  const value = enabled ? "true" : "false";
  if (Capacitor.isNativePlatform()) {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.set({ key: MARKETING_DEMO_PREFERENCE_KEY, value });
      return;
    } catch {
      // fall through
    }
  }
  localStorage.setItem(MARKETING_DEMO_PREFERENCE_KEY, value);
}

export function MarketingDemoProvider({ children }: { children: React.ReactNode }) {
  const anonymousDeviceId = useDeviceId();
  const platform = Capacitor.getPlatform();
  const isEligible = isMarketingDemoEligible(anonymousDeviceId, platform);
  const [ready, setReady] = useState(false);
  const [enabled, setEnabledState] = useState(false);

  const endDate = todayKey();
  const seedHeatmap = useQuery(
    seedHeatmapRef,
    isEligible
      ? { anonymousDeviceId, startDate: addDays(endDate, -60), endDate }
      : "skip",
  );

  const seedApps = useMemo(() => resolveMarketingSeedApps(seedHeatmap), [seedHeatmap]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const stored = await readMarketingDemoPreference();
      if (!cancelled) {
        setEnabledState(isEligible && stored);
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEligible]);

  useEffect(() => {
    if (!isEligible && enabled) {
      setEnabledState(false);
      void writeMarketingDemoPreference(false);
    }
  }, [enabled, isEligible]);

  const dataset = useMemo(() => {
    if (!isEligible || !enabled) return null;
    return generateMarketingDemoDataset(seedApps, endDate);
  }, [enabled, endDate, isEligible, seedApps]);

  const setEnabled = useCallback(async (next: boolean) => {
    if (!isEligible) {
      setEnabledState(false);
      await writeMarketingDemoPreference(false);
      return;
    }
    setEnabledState(next);
    await writeMarketingDemoPreference(next);
  }, [isEligible]);

  const disable = useCallback(async () => {
    setEnabledState(false);
    await writeMarketingDemoPreference(false);
  }, []);

  const isActive = isEligible && enabled && dataset != null;

  return (
    <MarketingDemoContext.Provider
      value={{
        isEligible,
        isActive,
        enabled,
        ready,
        dataset,
        seedApps,
        setEnabled,
        disable,
      }}
    >
      {children}
    </MarketingDemoContext.Provider>
  );
}

export function useMarketingDemoMode() {
  return useContext(MarketingDemoContext);
}

export function useIsMarketingDemoActive() {
  return useContext(MarketingDemoContext).isActive;
}

export function useMarketingDemoOverlay<T>(
  queryName: MarketingDemoQueryName,
  args: Record<string, unknown> | "skip",
  raw: T | undefined,
): T | undefined {
  const { isActive, dataset } = useMarketingDemoMode();
  return useMemo(() => {
    if (args === "skip" || !isActive || !dataset) return raw;
    return applyMarketingDemoOverlay(queryName, args, raw, dataset);
  }, [args, dataset, isActive, queryName, raw]);
}
